/**
 * Refactored FileExplorer Component
 *
 * This is a simplified version that demonstrates how to use the smaller components
 * and hooks. The original FileExplorer.tsx can be gradually migrated to use this structure.
 */

import { cn } from '@heroui/theme'
import { MediaType, useBulkAbortUploads, useBulkPermanentDelete, useBulkRestore } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore, useBillingStatusStore, useIsOverQuota } from '@qse/stores'
import {
  ContextMenu,
  ContextMenuAction,
  getFileExtension,
  getIconFromExtension,
  getIconFromMimeType,
  handleFilePreview,
  ListViewHeader,
  MobileActionDrawer,
  NewContextMenu,
  NewContextMenuAction,
  useFileManager,
  useFolderUpload,
  useGlobalFileUpload,
} from '@qse/ui'
import { type FileItem, truncateTextMiddle, useIsMobile } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useFileActions } from '@/hooks/use-file-actions'
import { useViewMode, ViewType } from '@/hooks/useViewMode'

import {
  FileExplorerContent,
  FileExplorerDragOverlay,
  FileExplorerEmptyState,
  FileExplorerErrorState,
  FileExplorerHeader,
  FileExplorerLoadingState,
  FileExplorerToolbar,
  MarqueeSelection,
} from './components'
import {
  useFileExplorerClipboard,
  useFileExplorerData,
  useFileExplorerDragDrop,
  useFileExplorerKeyboardShortcuts,
} from './hooks'

type SortKey = 'name' | 'size' | 'date' | 'trashedDate'
type SortOrder = 'ASC' | 'DESC'

// Maximum number of items that can be selected
const MAX_SELECTION_LIMIT = 1000

// Helper function to get icon path for drag preview
const getFileIconPath = (file: FileItem): string => {
  // Handle folder case first
  if (file.isDir || file.type === 'folder') {
    return '/assets/svg/folder.svg'
  }

  // Try to get icon from MIME type first
  if (file.type && file.type !== 'folder') {
    const iconPath = getIconFromMimeType(file.type)

    if (iconPath) {
      return iconPath
    }
  }

  // Try to get icon from mediaType prop
  if (file.mediaType && file.mediaType !== MediaType.FOLDER) {
    const iconPath = getIconFromMimeType(file.mediaType)

    if (iconPath) {
      return iconPath
    }
  }

  // Fallback to extension-based detection from filename
  const extension = getFileExtension(file.name)

  if (extension) {
    const iconPath = getIconFromExtension(extension)

    if (iconPath) {
      return iconPath
    }
  }

  // Final fallback to generic file icon
  return '/assets/svg/file-text.svg'
}

interface FileExplorerProps {
  title: string
  emptyStateConfig?: {
    icon: React.ReactNode
    title: string
    description?: string
  }
  searchParams?: {
    limit?: number
    offset?: number
    parentPath?: string
    parentId?: string
    search?: string
    mediaType?: MediaType
    sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
    sortOrder?: 'ASC' | 'DESC'
    location?: 'HOME' | 'FAVORITE' | 'TRASH'
  }
  setItemsNumber?: (itemsNumber: number) => void
  files?: FileItem[]
  showSortOptions?: boolean
  showViewToggle?: boolean
  showSelectionToolbar?: boolean
  showRestore?: boolean
  trashMode?: boolean
  uploadEnabled?: boolean
  parentId?: string
  parentName?: string
  parentType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
  breadcrumbItems?: Array<{ id: string; name: string; path?: string }>
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
  onFileDoubleClick?: (file: FileItem) => void
  isInView?: boolean
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  title,
  emptyStateConfig,
  searchParams,
  setItemsNumber,
  files: externalFiles,
  showSortOptions = true,
  showViewToggle = true,
  showSelectionToolbar = true,
  showRestore = false,
  trashMode = false,
  uploadEnabled = true,
  parentId,
  parentName,
  parentType,
  breadcrumbItems,
  isLoading: externalIsLoading,
  error: externalError,
  onRetry: externalOnRetry,
  hasNextPage: externalHasNextPage,
  isFetchingNextPage: externalIsFetchingNextPage,
  onLoadMore: externalOnLoadMore,
  onFileDoubleClick,
  isInView = false,
}) => {
  const navigate = useNavigate()
  const isOverQuota = useIsOverQuota()
  const location = useLocation()
  const params = useParams<{ id: string }>()
  const uploadStore = useGlobalFileUpload()
  const { addFiles, handleAbort } = uploadStore
  const queryClient = useQueryClient()
  const { showPaymentOverdueBanner } = useBillingStatusStore()
  const { fileInputRef, handleFolderUpload, handleFileChange } = useFolderUpload({
    pathname: location.pathname,
    currentFolderId: params.id,
    queryClient,
    addFiles,
  })

  const { viewMode, setViewMode } = useViewMode()
  const isMobile = useIsMobile()
  // Sort state management
  const [sortOption, setSortOption] = useState<SortKey>(() => {
    const saved = localStorage.getItem('fileExplorerSortOption')

    return saved === 'name' || saved === 'size' || saved === 'date' || saved === 'trashedDate'
      ? (saved as SortKey)
      : 'name'
  })

  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('fileExplorerSortOrder')

    return saved === 'ASC' || saved === 'DESC' ? (saved as SortOrder) : 'ASC'
  })

  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Dynamic height based on current route
  const SCROLL_HEIGHT_DEFAULT = 'calc(100dvh - 225px)' as const
  const getScrollHeight = (pathname: string, viewMode: ViewType): string | null => {
    if (pathname.startsWith('/folders')) {
      return viewMode === 'list' ? 'calc(100dvh - 300px)' : 'calc(100dvh - 275px)'
    }
    if (pathname.startsWith('/dashboard/favorites')) {
      return viewMode === 'list' ? 'calc(100dvh - 300px)' : 'calc(100dvh - 250px)'
    }
    if (pathname.startsWith('/dashboard/trash')) {
      return viewMode === 'list'
        ? showPaymentOverdueBanner
          ? 'calc(100dvh - 405px)'
          : 'calc(100dvh - 305px)'
        : showPaymentOverdueBanner
          ? 'calc(100dvh - 255px)'
          : 'calc(100dvh - 255px)'
    }
    if (pathname.startsWith('/dashboard/search-results')) {
      return viewMode === 'list' ? 'calc(100dvh - 325px)' : 'calc(100dvh - 275px)'
    }
    if (pathname === '/dashboard') {
      return null
    }

    return SCROLL_HEIGHT_DEFAULT
  }
  const scrollHeight = useMemo<string | null>(
    () => getScrollHeight(location.pathname, viewMode),
    [location.pathname, viewMode],
  )

  // New context menu state for right-click on empty space
  const [newContextMenu, setNewContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
  })

  // API hooks
  const { mutateAsync: deleteForever } = useBulkPermanentDelete()
  const { mutateAsync: restoreFiles } = useBulkRestore()
  const { mutateAsync: abortUploads } = useBulkAbortUploads()

  // Custom hooks for data and functionality
  const {
    files,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    isFetchingNextPage,
    onRetry,
    onLoadMore,
    defaultSearchParams,
  } = useFileExplorerData({
    searchParams,
    externalFiles,
    externalIsLoading,
    externalError,
    externalOnRetry,
    externalHasNextPage,
    externalIsFetchingNextPage,
    externalOnLoadMore,
    sortOption,
    sortOrder,
    trashMode,
  })

  useEffect(() => {
    if (files && setItemsNumber) {
      setItemsNumber?.(files.length)
    }
  }, [files, setItemsNumber])

  // File manager for state management
  const fm = useFileManager(files)

  // File actions hook
  const {
    handleBulkCopy: handleBulkCopyOriginal,
    handleBulkToggleFavorite: handleBulkToggleFavoriteOriginal,
    handleBulkDownload: handleBulkDownloadOriginal,
    handleRename: handleRenameOriginal,
    handleBulkDelete: handleBulkDeleteOriginal,
    handleBulkRestore: handleBulkRestoreOriginal,
    handleBulkMove,
    handleSelectionChange: handleSelectionChangeOriginal,
  } = useFileActions(files)

  // Wrapper functions for bulk operations
  const handleBulkCopy = (selectedFileIds: Set<string | number>) => {
    const fileIds = Array.from(selectedFileIds).map(id => id.toString())

    handleBulkCopyOriginal(fileIds)
  }

  const handleBulkDelete = (selectedFileIds: Set<string | number>) => {
    const fileIds = Array.from(selectedFileIds).map(id => id.toString())

    handleBulkDeleteOriginal(fileIds)
  }

  const handleBulkDownload = (selectedFileIds: Set<string | number>) => {
    const fileIds = Array.from(selectedFileIds).map(id => id.toString())

    handleBulkDownloadOriginal(fileIds)
  }

  const handleBulkToggleFavorite = (selectedFileIds: Set<string | number>) => {
    const fileIds = Array.from(selectedFileIds).map(id => id.toString())

    handleBulkToggleFavoriteOriginal(fileIds)
  }

  const handleBulkRestore = (selectedFileIds: Set<string | number>) => {
    const fileIds = Array.from(selectedFileIds).map(id => id.toString())

    handleBulkRestoreOriginal(fileIds)
  }

  const handleBulkDeleteForever = useCallback(
    async (selectedFileIds: Set<string | number>) => {
      const fileIds = Array.from(selectedFileIds).map(id => id.toString())

      useActiveModelStore.setState({
        width: 'max-w-[490px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        data: {
          title:
            selectedFileIds.size === 1
              ? 'Delete Forever this item?'
              : `Delete Forever ${selectedFileIds.size} items?`,
          message:
            selectedFileIds.size === 1
              ? 'This file or folder will be permanently deleted and cannot be restored.'
              : 'These files and folders will be permanently deleted and cannot be restored.',
          icon: '/assets/svg/warning.svg',
          actions: [
            { label: 'Cancel', variant: 'flat' },
            {
              label: 'Delete Forever',
              color: 'danger',
              variant: 'solid',
              onPress: async () => {
                try {
                  await deleteForever({ fileIds })
                } catch (_err) {
                  toast.error('Failed to delete files')
                }
              },
            },
          ],
        },
        toggleModel: true,
      })
    },
    [deleteForever],
  )

  const handleRename = (fileId: string | number, path?: string) => {
    handleRenameOriginal([fileId.toString()], path || '')
  }

  // Create folder handler
  const handleCreateFolder = useCallback(() => {
    const currentFolderId = parentId || ''
    const currentPath = location.pathname
    const currentFolderName =
      breadcrumbItems && breadcrumbItems.length > 0
        ? breadcrumbItems[breadcrumbItems.length - 1].name
        : parentName

    useActiveModelStore.setState({
      data: {
        parentId: currentFolderId,
        parentPath: currentPath.startsWith('/folders/') ? currentPath : '/',
        parentName: currentFolderName,
        onSuccess: (_folderName: string) => {
          // Folder will be automatically added through API cache invalidation
        },
        modalHeader: {
          title: 'Create Folder',
          showCloseButton: true,
        },
      },
      width: 'max-w-[490px]',
      modelActive: ModelControlEnum.CREATE_FOLDER,
      toggleModel: true,
      scrollBehavior: 'inside',
    })
  }, [parentId, parentName, breadcrumbItems, location.pathname])

  // New context menu action handlers
  const handleNewContextMenuAction = useCallback(
    (action: NewContextMenuAction) => {
      switch (action) {
        case 'new-folder': {
          handleCreateFolder()
          break
        }
        case 'file-upload': {
          // Trigger file upload - this will be handled by the existing file input
          const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement

          if (fileInput) {
            fileInput.webkitdirectory = false
            fileInput.multiple = true
            fileInput.click()
          }
          break
        }
        case 'folder-upload': {
          handleFolderUpload()
          break
        }
      }
    },
    [handleCreateFolder, handleFolderUpload],
  )

  // Clipboard functionality
  const { clipboardFileIds, handleClipboardCopy, handleClipboardPaste, clearClipboard } =
    useFileExplorerClipboard(fm.selectedFiles, handleBulkCopyOriginal, files)

  // Drag and drop functionality with proper type conversion
  const { isDragOver, handleDrop, handleDragEvents } = useFileExplorerDragDrop({
    files,
    uploadEnabled: uploadEnabled && !isOverQuota,
    parentId,
    searchParams: defaultSearchParams,
    pathname: location.pathname,
    parentType,
    onFolderDrop: async (fileIds: string[], targetFolderId: string | number) => {
      await handleBulkMove(fileIds, targetFolderId.toString(), undefined)
    },
    setIsDragOver: (_isDragOver: boolean) => {
      // This will be handled internally by the hook
    },
  })

  // Wrapper function to clear dragOverIndex after drop
  const handleFileDrop = useCallback(
    async (e: React.DragEvent<Element>, index: number) => {
      await handleDrop(e, index)
      // Clear dragOverIndex after drop operation completes
      fm.setDragOverIndex(null)
    },
    [handleDrop, fm],
  )

  // File interaction handlers
  const handleFileClick = (fileId: string | number, e: React.MouseEvent) => {
    // Disable all selection in CHAT_ROOT folders (unless in trash mode)
    if (parentType === 'CHAT_ROOT' && !trashMode) return

    // Disable selection for CHAT_CONVERSATION folders
    const file = files.find(f => f.id === fileId)

    if (file?.systemFolderType === 'CHAT_CONVERSATION' && !trashMode) return

    if (e.ctrlKey || e.metaKey) {
      const newSelectedFiles = new Set(fm.selectedFiles)

      if (newSelectedFiles.has(fileId)) {
        newSelectedFiles.delete(fileId)
      } else {
        // Check if adding this file would exceed the limit
        if (newSelectedFiles.size >= MAX_SELECTION_LIMIT) {
          toast.error(`Cannot select more than ${MAX_SELECTION_LIMIT} items`)

          return
        }
        newSelectedFiles.add(fileId)
      }
      fm.setSelectedFiles(newSelectedFiles)
    } else if (e.shiftKey && lastClickedIndex !== null) {
      const currentIndex = sortedFiles.findIndex(f => f.id === fileId)

      if (currentIndex !== -1) {
        const start = Math.min(lastClickedIndex, currentIndex)
        const end = Math.max(lastClickedIndex, currentIndex)
        const rangeFiles = sortedFiles.slice(start, end + 1)

        // Check if the range selection would exceed the limit
        if (rangeFiles.length > MAX_SELECTION_LIMIT) {
          toast.error(`Cannot select more than ${MAX_SELECTION_LIMIT} items`)

          return
        }
        const newSelectedFiles = new Set(rangeFiles.map(f => f.id))

        fm.setSelectedFiles(newSelectedFiles)
      }
    } else {
      fm.setSelectedFiles(new Set([fileId]))
    }

    const clickedIndex = sortedFiles.findIndex(f => f.id === fileId)

    setLastClickedIndex(clickedIndex !== -1 ? clickedIndex : null)
  }

  const handleFileDoubleClick = (fileId: string | number) => {
    const file = files.find(f => f.id === fileId)

    if (onFileDoubleClick && file) {
      onFileDoubleClick(file)

      if (!file.isDir) {
        // Don't open preview for files that are still uploading or encrypting
        if (file.status === 'UPLOADING' || file.status === 'ENCRYPTING') {
          return
        }

        // Open preview for all file types (supported and unsupported)
        handleFilePreview(
          fileId,
          file.name,
          file.mediaType || MediaType.OTHER,
          file.isDir || false,
          file.isFavorite || false,
          file.type,
        )

        return
      }

      return
    }

    if (file && file.isDir) {
      // Navigate to the folder on double click, passing folder data to avoid refetch
      navigate(`/folders/${fileId}`, { state: { folderDetails: file } })
    } else if (file && !file.isDir) {
      // Don't open preview for files that are still uploading or encrypting
      if (file.status === 'UPLOADING' || file.status === 'ENCRYPTING') {
        return
      }

      // Open preview for all file types (supported and unsupported)
      handleFilePreview(
        fileId,
        file.name,
        file.mediaType || MediaType.OTHER,
        file.isDir || false,
        file.isFavorite || false,
        file.type,
      )
    }
  }

  const handleSelectionChange = (fileId: string | number, selected: boolean) => {
    // Check if selecting this file would exceed the limit
    if (selected && fm.selectedFiles.size >= MAX_SELECTION_LIMIT) {
      toast.error(`Cannot select more than ${MAX_SELECTION_LIMIT} items`)

      return
    }
    handleSelectionChangeOriginal(fileId, selected)
  }

  // Context menu handler
  const handleContextMenuAction = useCallback(
    (action: ContextMenuAction, fileId: string | number | null, path?: string) => {
      let selectedFileIds: Set<string | number>

      if (fileId != null && fm.selectedFiles.size > 0 && fm.selectedFiles.has(fileId)) {
        selectedFileIds = new Set(
          Array.from(fm.selectedFiles).filter(id => id != null) as (string | number)[],
        )
      } else if (fileId != null) {
        selectedFileIds = new Set([fileId])
      } else {
        selectedFileIds = new Set()
      }

      switch (action) {
        case 'preview':
          if (selectedFileIds.size === 1) {
            const fileId = Array.from(selectedFileIds)[0]
            const file = files.find(f => f.id === fileId)

            if (file) {
              handleFilePreview(
                fileId,
                file.name,
                file.mediaType || MediaType.OTHER,
                file.isDir || false,
                file.isFavorite || false,
                file.type,
              )
            }
          }
          break
        case 'trash':
          handleBulkDelete(selectedFileIds)
          break
        case 'duplicate':
          handleBulkCopy(selectedFileIds)
          break
        case 'rename':
          if (selectedFileIds.size === 1) {
            handleRename(Array.from(selectedFileIds)[0], path)
          }
          break
        case 'favourite':
          handleBulkToggleFavorite(selectedFileIds)
          break
        case 'download':
          handleBulkDownload(selectedFileIds)
          break
        case 'restore':
          if (showRestore) {
            handleBulkRestore(selectedFileIds)
          }
          break
        case 'deletePermanently': {
          // Handle permanent deletion of ghost files
          const fileIds = Array.from(selectedFileIds).map(id => id.toString())

          deleteForever({ fileIds })
          break
        }
        case 'move': {
          const selectedItems = Array.from(selectedFileIds)
            .map(id => {
              const file = files.find(f => f.id === id)

              if (!file) return null

              return {
                id: file.id.toString(),
                path: file.path || `/${file.name}`,
                name: file.name.length > 30 ? truncateTextMiddle(file.name, 30) : file.name,
                isDir: file.type === 'folder',
                status: 'ACTIVE',
                sizeBytes: file.size,
                isFavorite: file.isFavorite || false,
                parentId: file.parentId, // Include parentId from file data
                mimeType: file.type === 'folder' ? undefined : file.type,
                mediaType: file.type === 'folder' ? 'FOLDER' : 'OTHER',
                createdAt: file.lastModified,
                updatedAt: file.lastModified,
              }
            })
            .filter(Boolean)

          useActiveModelStore.setState({
            data: {
              selectedItems,
              currentFolderId: parentId,
              currentFolderPath: breadcrumbItems?.[breadcrumbItems.length - 1]?.path,
              parentId,
            },
            width: 'max-w-[650px]',
            scrollBehavior: 'outside',
            modelActive: ModelControlEnum.MOVE_ITEMS,
            toggleModel: true,
          })
          break
        }
      }
    },
    [
      handleBulkDelete,
      handleBulkCopy,
      handleRename,
      handleBulkToggleFavorite,
      handleBulkDownload,
      handleBulkRestore,
      showRestore,
      files,
      fm.selectedFiles,
    ],
  )

  // Single file action handlers
  const handleRestoreSingle = async (file: FileItem) => {
    try {
      await restoreFiles({ fileIds: [file.id.toString()] })
      toast.success('File restored successfully')
    } catch (_err) {
      toast.error('Failed to restore file')
    }
  }

  const handleDeleteForeverSingle = async (file: FileItem) => {
    try {
      await deleteForever({ fileIds: [file.id.toString()] })
      toast.success('File permanently deleted')
    } catch (_err) {
      toast.error('Failed to delete file')
    }
  }

  // Async wrapper functions for SelectionToolbar compatibility
  const handleBulkCopyAsync = async (fileIds: string[]) => {
    handleBulkCopyOriginal(fileIds)
  }

  const handleBulkDeleteAsync = async (fileIds: string[], onSuccess?: () => void) => {
    handleBulkDeleteOriginal(fileIds)
    onSuccess?.()
  }

  const handleBulkDownloadAsync = async (fileIds: string[]) => {
    handleBulkDownloadOriginal(fileIds)
  }

  const handleRestoreAsync = async (fileIds: string[]) => {
    handleBulkRestoreOriginal(fileIds)
  }

  const handleRenameAsync = async (fileIds: string[]) => {
    if (fileIds.length > 0) {
      handleRenameOriginal(fileIds, '')
    }
  }

  // Sort and view handlers
  const handleSortOptionChange = useCallback((option: string) => {
    setSortOption(option as SortKey)
    localStorage.setItem('fileExplorerSortOption', option)
  }, [])

  const handleSortOrderToggle = () => {
    setSortOrder((prev: SortOrder) => {
      const next: SortOrder = prev === 'ASC' ? 'DESC' : 'ASC'

      localStorage.setItem('fileExplorerSortOrder', next)

      return next
    })
  }

  const handleViewModeChange = (mode: string) => {
    setViewMode(mode as ViewType)
    fm.setContextMenu(prev => ({ ...prev, isOpen: false }))
  }

  // Drag handlers
  const handleDragStart = (e: React.DragEvent<Element>, fileId?: string | number) => {
    let dragFileId: string | number
    let ids: (string | number)[]

    // If fileId is provided as parameter (grid view), use it directly
    if (fileId !== undefined) {
      dragFileId = fileId
      ids = fm.selectedFiles.has(fileId) ? Array.from(fm.selectedFiles) : [fileId]
    } else {
      // Fallback to getting fileId from data-file-id attribute (for list view)
      const dataFileId = e.currentTarget.getAttribute('data-file-id')

      dragFileId = dataFileId ? (isNaN(Number(dataFileId)) ? dataFileId : Number(dataFileId)) : 0

      if (dragFileId === 0) {
        const fileCard = e.currentTarget.closest('.file-card')

        if (fileCard) {
          const cardFileId = fileCard.getAttribute('data-file-id')

          if (cardFileId) {
            dragFileId = isNaN(Number(cardFileId)) ? cardFileId : Number(cardFileId)
          }
        }
      }

      ids = fm.selectedFiles.has(dragFileId) ? Array.from(fm.selectedFiles) : [dragFileId]
    }

    // Check if any of the dragged files are system folders
    const draggedFiles = files.filter(f => ids.includes(f.id))
    const hasSystemFolders = draggedFiles.some(f => f.systemFolderType)

    if (hasSystemFolders) {
      e.preventDefault()

      return
    }

    // Set drag state and data transfer

    fm.setDraggedFiles(ids)
    fm.setIsDraggingFile(true)

    const dataToTransfer = JSON.stringify(ids)

    e.dataTransfer.setData('text/plain', dataToTransfer)
    e.dataTransfer.setData('application/json', JSON.stringify({ fileIds: ids, type: 'qse-files' }))
    e.dataTransfer.effectAllowed = 'move'

    // Create a completely transparent drag image to hide the original card
    const transparentImage = new Image()

    transparentImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='

    // Set the transparent image as drag image with proper offset
    // This should completely hide the browser's default drag image
    e.dataTransfer.setDragImage(transparentImage, 0, 0)

    // Additional attempt to prevent default drag image
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.dropEffect = 'move'

    // Prevent the default drag image by setting empty data for other formats
    e.dataTransfer.setData('text/html', '')
    e.dataTransfer.setData('text/uri-list', '')

    // Alternative approach: create an invisible element and use it as drag image
    const invisibleElement = document.createElement('div')

    invisibleElement.style.position = 'absolute'
    invisibleElement.style.left = '-9999px'
    invisibleElement.style.top = '-9999px'
    invisibleElement.style.width = '1px'
    invisibleElement.style.height = '1px'
    invisibleElement.style.opacity = '0'
    invisibleElement.style.pointerEvents = 'none'
    invisibleElement.style.userSelect = 'none'
    document.body.appendChild(invisibleElement)

    // Use the invisible element as drag image
    e.dataTransfer.setDragImage(invisibleElement, 0, 0)

    // Clean up the invisible element after a short delay
    setTimeout(() => {
      if (document.body.contains(invisibleElement)) {
        document.body.removeChild(invisibleElement)
      }
    }, 100)

    // Create a simple HTML-based floating drag preview
    const selectedFiles = files.filter(f => ids.includes(f.id))
    const floatingPreview = document.createElement('div')

    floatingPreview.id = 'floating-drag-preview'
    floatingPreview.style.position = 'fixed'
    floatingPreview.style.pointerEvents = 'none'
    floatingPreview.style.zIndex = '9999'
    floatingPreview.style.transition = 'none'
    floatingPreview.style.backgroundColor = 'white'
    floatingPreview.style.border = '1px solid #d1d5db'
    floatingPreview.style.borderRadius = '6px'
    floatingPreview.style.padding = '6px 8px'
    floatingPreview.style.boxShadow =
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    floatingPreview.style.fontSize = '12px'
    floatingPreview.style.fontWeight = '500'
    floatingPreview.style.color = '#111827'
    floatingPreview.style.maxWidth = '180px'
    floatingPreview.style.whiteSpace = 'nowrap'
    floatingPreview.style.overflow = 'hidden'
    floatingPreview.style.textOverflow = 'ellipsis'

    // Create the content
    if (selectedFiles.length === 1) {
      const file = selectedFiles[0]
      const iconPath = getFileIconPath(file)

      floatingPreview.innerHTML = `
        <div style="display: flex; align-items: center; gap: 6px;">
          <img src="${iconPath}" alt="File icon" style="width: 16px; height: 16px; flex-shrink: 0;" />
          <span>${file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}</span>
        </div>
      `
    } else {
      floatingPreview.innerHTML = `
        <div style="display: flex; align-items: center; gap: 6px;">
          <img src="/assets/svg/file-text.svg" alt="Multiple files" style="width: 16px; height: 16px; flex-shrink: 0;" />
          <span>Moving ${selectedFiles.length} items</span>
        </div>
      `
    }

    document.body.appendChild(floatingPreview)

    // Set initial position
    floatingPreview.style.left = e.clientX + 10 + 'px'
    floatingPreview.style.top = e.clientY - 10 + 'px'

    // Track mouse movement to update preview position
    const handleMouseMove = (e: MouseEvent) => {
      // Use requestAnimationFrame for smooth movement
      requestAnimationFrame(() => {
        floatingPreview.style.left = e.clientX + 10 + 'px'
        floatingPreview.style.top = e.clientY - 10 + 'px'
      })
    }

    // Track drag over events as well (more reliable during drag)
    const handleDragOver = (e: DragEvent) => {
      if (e.clientX && e.clientY) {
        requestAnimationFrame(() => {
          floatingPreview.style.left = e.clientX + 10 + 'px'
          floatingPreview.style.top = e.clientY - 10 + 'px'
        })
      }
    }

    // Add both listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true })
    document.addEventListener('dragover', handleDragOver, { passive: true })

    // Store cleanup function to be called in handleDragEnd
    ;(e.target as any)._dragCleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('dragover', handleDragOver)
      if (document.body.contains(floatingPreview)) {
        document.body.removeChild(floatingPreview)
      }
    }

    // Add global drag end listener as backup
    const globalDragEndHandler = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('dragend', globalDragEndHandler)
      if (document.body.contains(floatingPreview)) {
        document.body.removeChild(floatingPreview)
      }
    }

    document.addEventListener('dragend', globalDragEndHandler)
  }

  const handleDragEnd = () => {
    fm.setDraggedFiles([])
    fm.setIsDraggingFile(false)
    fm.setDragOverIndex(null)
    fm.dragCounter.current = 0

    // Clean up floating drag preview
    const floatingPreview = document.getElementById('floating-drag-preview')

    if (floatingPreview) {
      document.body.removeChild(floatingPreview)
    }

    // Remove any stored cleanup functions
    const eventTarget = event?.target as any

    if (eventTarget?._dragCleanup) {
      eventTarget._dragCleanup()
      delete eventTarget._dragCleanup
    }

    // Also clean up any remaining mousemove listeners
    document.removeEventListener('mousemove', () => {})
  }

  const handleDragEnter = (e: React.DragEvent<Element>) => {
    e.preventDefault()
    fm.dragCounter.current++
  }

  const handleDragLeave = () => {
    fm.dragCounter.current--
    if (fm.dragCounter.current === 0) fm.setDragOverIndex(null)
  }

  const handleDragOver = (e: React.DragEvent<Element>, index: number) => {
    e.preventDefault()

    // Check if we're dragging over a folder and if any dragged item is the same folder
    const targetFile = sortedFiles[index]

    // Check if this is an external drag (upload) vs internal drag (move)
    // External drags have 'Files' type but not 'text/plain' (our internal move marker)
    const isExternalDrag =
      e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('text/plain')

    // Prevent drag-over visual feedback for external uploads when over quota
    // Internal moves are allowed since they don't increase quota
    if (isOverQuota && isExternalDrag) {
      e.dataTransfer.dropEffect = 'none'
      fm.setDragOverIndex(null)

      return
    }

    // Prevent dropping into system folders
    if (targetFile && targetFile.systemFolderType) {
      e.dataTransfer.dropEffect = 'none'
      fm.setDragOverIndex(null)

      return
    }

    if (targetFile && targetFile.type === 'folder') {
      // Try to get dragged IDs from dataTransfer
      let draggedIds: (string | number)[] = []

      try {
        const textData = e.dataTransfer.getData('text/plain')

        if (textData) {
          const parsed = JSON.parse(textData)

          if (Array.isArray(parsed)) {
            draggedIds = parsed
          }
        }
      } catch {
        // Ignore parsing errors
      }

      // Check if any dragged item is the same as the target
      const isDroppingFolderIntoItself = draggedIds.some(
        draggedId => draggedId.toString() === targetFile.id.toString(),
      )

      if (isDroppingFolderIntoItself) {
        e.dataTransfer.dropEffect = 'none'
        fm.setDragOverIndex(null) // Clear drag over state when drop is not allowed

        return
      }
    }

    fm.setDragOverIndex(index)
  }

  // Context menu action handler
  const onAction = useCallback(
    async (action: ContextMenuAction) => {
      handleContextMenuAction(action, fm.contextMenu.fileId, fm.contextMenu.path || '')
      fm.setContextMenu(prev => ({ ...prev, isOpen: false }))
    },
    [handleContextMenuAction, fm.contextMenu.fileId, fm.contextMenu.path, fm.setContextMenu],
  )

  // Sorted files - remove duplicates by ID to handle pagination issues
  const sortedFiles = useMemo(() => {
    // Remove any duplicate files by ID
    const uniqueFilesMap = new Map()

    files.forEach(file => {
      if (!uniqueFilesMap.has(file.id)) {
        uniqueFilesMap.set(file.id, file)
      }
    })
    let result = Array.from(uniqueFilesMap.values())

    // Sort if using external files
    if (externalFiles) {
      result = [...result].sort((a, b) => {
        if (a.type === 'folder' && b.type !== 'folder') return -1
        if (a.type !== 'folder' && b.type === 'folder') return 1

        return a.name.localeCompare(b.name)
      })
    }

    return result
  }, [files, externalFiles])

  // Handle select all with limit enforcement
  const handleSelectAll = useCallback(() => {
    // Filter out system folders from selection (unless in trash mode)
    const selectableFiles = sortedFiles.filter(file => {
      if (file.systemFolderType && !trashMode) {
        return false
      }

      return true
    })

    if (selectableFiles.length > MAX_SELECTION_LIMIT) {
      toast.error(
        `Cannot select more than ${MAX_SELECTION_LIMIT} items. Only the first ${MAX_SELECTION_LIMIT} items will be selected.`,
      )
      // Select only the first MAX_SELECTION_LIMIT items
      const limitedSelection = new Set(selectableFiles.slice(0, MAX_SELECTION_LIMIT).map(f => f.id))

      fm.setSelectedFiles(limitedSelection)
    } else {
      // Select all selectable items if within limit
      const allSelection = new Set(selectableFiles.map(f => f.id))

      fm.setSelectedFiles(allSelection)
    }
  }, [sortedFiles, fm.setSelectedFiles, trashMode])

  // Keyboard shortcuts
  useFileExplorerKeyboardShortcuts({
    files,
    selectedFiles: fm.selectedFiles,
    setSelectedFiles: fm.setSelectedFiles,
    onBulkDelete: handleBulkDelete,
    onBulkDeleteForever: handleBulkDeleteForever,
    onClipboardCopy: handleClipboardCopy,
    onClipboardPaste: handleClipboardPaste,
    onCreateFolder: handleCreateFolder,
    onRename: handleRename,
    onSelectAll: handleSelectAll,
    parentType,
    trashMode,
  })

  // Reset file manager state when files change
  useEffect(() => {
    fm.setFiles(files)
    fm.setSelectedFiles(new Set()) // Reset selection when navigating to new folder
    fm.setDraggedFiles([])
    fm.setDragOverIndex(null)
    fm.setIsDraggingFile(false)
    fm.setIsMarqueeSelecting(false)
    fm.setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      fileId: null,
      path: null,
      submenuOpen: null,
    })
    setNewContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
    })
    setLastClickedIndex(null)

    return () => {
      fm.setFiles([])
      fm.setSelectedFiles(new Set()) // Reset selection on cleanup
      fm.setDraggedFiles([])
      fm.setDragOverIndex(null)
      fm.setIsDraggingFile(false)
      fm.setIsMarqueeSelecting(false)
      fm.setContextMenu({
        isOpen: false,
        position: { x: 0, y: 0 },
        fileId: null,
        path: null,
        submenuOpen: null,
      })
      setNewContextMenu({
        isOpen: false,
        position: { x: 0, y: 0 },
      })
      setLastClickedIndex(null)
      clearClipboard()
    }
  }, [files, clearClipboard])

  // Auto-fetch next page when all visible items are deleted but more exist
  useEffect(() => {
    if (sortedFiles.length === 0 && hasNextPage && !isFetchingNextPage && !isLoading) {
      onLoadMore()
    }
  }, [sortedFiles.length, hasNextPage, isFetchingNextPage, isLoading, onLoadMore])

  // Context menu click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!fm.contextMenu.isOpen) return

      const target = e.target as HTMLElement

      if (
        target.closest('.context-menu') ||
        target.closest('[data-slot="content"]') ||
        target.closest('[role="dialog"]') ||
        target.closest('[data-overlay-container]')
      ) {
        return
      }

      fm.setContextMenu(prev => ({ ...prev, isOpen: false }))
    }

    if (fm.contextMenu.isOpen) {
      document.addEventListener('click', handleClickOutside)

      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [fm.contextMenu.isOpen, fm.setContextMenu])

  // Close new context menu when marquee selection starts
  useEffect(() => {
    if (fm.isMarqueeSelecting && newContextMenu.isOpen) {
      setNewContextMenu(prev => ({ ...prev, isOpen: false }))
    }
  }, [fm.isMarqueeSelecting, newContextMenu.isOpen])

  // Infinite scroll
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => {
        const [entry] = entries

        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore()
        }
      },
      { root: null, rootMargin: '200px' },
    )

    if (bottomRef.current) observer.observe(bottomRef.current)

    return () => {
      if (bottomRef.current) observer.unobserve(bottomRef.current)
    }
  }, [onLoadMore, hasNextPage, isFetchingNextPage])

  const onContextMenu = (e: React.MouseEvent) => {
    // Only show new context menu if right-clicking on empty space (not on files)
    const target = e.target as HTMLElement
    const onCard = !!target.closest('.file-card') || !!target.closest('[data-file-id]')

    if (!onCard) {
      // Check if we're inside a system folder using the parentType prop
      if (parentType) {
        e.preventDefault()

        return
      }

      e.preventDefault()
      // Close any existing file context menu
      fm.setContextMenu(prev => ({ ...prev, isOpen: false }))
      setNewContextMenu({
        isOpen: true,
        position: { x: e.clientX, y: e.clientY },
      })
    }
  }

  // Show loading state - either initial load or when fetching new data (like sorting)
  if ((isLoading && !files.length) || (isFetching && !files.length)) {
    return <FileExplorerLoadingState viewMode={viewMode} />
  }

  // Show empty state only if not currently fetching and no files and no more pages
  if (sortedFiles.length === 0 && emptyStateConfig && !isFetching && !hasNextPage) {
    return (
      <div
        className="relative overflow-y-scroll scrollbar-hide h-full"
        role="grid"
        style={{ height: 'calc(100dvh - 200px)' }}
        onContextMenu={onContextMenu}
      >
        <FileExplorerEmptyState
          emptyStateConfig={emptyStateConfig}
          parentId={parentId}
          parentType={parentType}
          searchParams={defaultSearchParams}
          uploadEnabled={uploadEnabled}
        />
        {!trashMode && (
          <NewContextMenu
            isOpen={newContextMenu.isOpen}
            position={newContextMenu.position}
            onAction={handleNewContextMenuAction}
            onClose={() => setNewContextMenu(prev => ({ ...prev, isOpen: false }))}
            onRequestClose={() => setNewContextMenu(prev => ({ ...prev, isOpen: false }))}
          />
        )}
      </div>
    )
  }

  // Show error state
  if (error && !files.length) {
    return <FileExplorerErrorState error={error} onRetry={onRetry} />
  }

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-full"
      style={{
        minHeight: 'calc(100dvh - 300px)',
      }}
      {...handleDragEvents}
      role="grid"
      onContextMenu={onContextMenu}
    >
      <MarqueeSelection
        containerRef={fm.containerRef}
        end={fm.marqueeEnd}
        finalize={fm.finalizeMarqueeSelection}
        isSelecting={fm.isMarqueeSelecting}
        parentType={parentType}
        setEnd={fm.setMarqueeEnd}
        setIsSelecting={fm.setIsMarqueeSelecting}
        setStart={fm.setMarqueeStart}
        start={fm.marqueeStart}
        trashMode={trashMode}
        shouldStartOnTarget={ev => {
          if (fm.isDraggingFile || fm.contextMenu.isOpen || newContextMenu.isOpen) return false
          const target = ev.target as HTMLElement
          const onCard = !!target.closest('.file-card') || !!target.closest('[data-file-id]')
          const onSelectionToolbar =
            !!target.closest('.selection-toolbar') || !!target.closest('[data-selection-toolbar]')

          if (!onCard && !onSelectionToolbar) {
            const e = ev as React.MouseEvent

            if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
              fm.setSelectedFiles(new Set())
              setLastClickedIndex(null)
            }
          }

          return !onCard && !onSelectionToolbar
        }}
      >
        <div
          className={cn(
            'sticky z-20  dark:bg-black bg-gray-50 pb-2 md:pt-3',
            showPaymentOverdueBanner && !isInView ? 'lg:top-44 top-[282px] ' : 'lg:top-28 top-40 ',
          )}
        >
          <div className="flex flex-col gap-2 drive-container mx-auto md:px-[30px] px-4 md:mt-2">
            <div className="relative md:h-12 flex items-center gap-2">
              {!isMobile && fm.selectedFiles.size > 0 && (
                <FileExplorerToolbar
                  allFiles={fm.files}
                  currentFolderId={parentId}
                  currentFolderPath={breadcrumbItems?.[breadcrumbItems.length - 1]?.path}
                  parentId={parentId}
                  selectedFiles={fm.selectedFiles}
                  showSelectionToolbar={showSelectionToolbar}
                  trashMode={trashMode}
                  onCopy={handleBulkCopyAsync}
                  onDelete={handleBulkDeleteAsync}
                  onDownload={handleBulkDownloadAsync}
                  onRename={handleRenameAsync}
                  onReset={() => fm.setSelectedFiles(new Set())}
                  onRestore={handleRestoreAsync}
                  onToggleFavorite={handleBulkToggleFavoriteOriginal}
                  onDeleteForever={async (fileIds: string[]) => {
                    await deleteForever({ fileIds })
                  }}
                  onDeleteGhostFiles={async (fileIds: string[]) => {
                    // Update frontend upload tracker state for each file
                    for (const serverFileId of fileIds) {
                      // Find the uploading file by serverFileId
                      const uploadingFile = uploadStore.uploadingFiles.find(
                        f => f.serverFileId === serverFileId,
                      )

                      if (uploadingFile) {
                        await handleAbort(uploadingFile.id as number)
                      }
                    }
                    // Also call API to abort on backend
                    await abortUploads({ fileIds })
                  }}
                />
              )}
              <FileExplorerHeader
                isLoading={isFetching}
                showSortOptions={showSortOptions}
                showTitle={isMobile || fm.selectedFiles.size === 0}
                showViewToggle={showViewToggle}
                sortOption={sortOption}
                sortOrder={sortOrder}
                title={title}
                trashMode={trashMode}
                viewMode={viewMode}
                onSortOptionChange={handleSortOptionChange}
                onSortOrderToggle={handleSortOrderToggle}
                onViewModeChange={handleViewModeChange}
              />
            </div>
            {viewMode === 'list' && <ListViewHeader trashMode={trashMode} />}
          </div>
        </div>

        <div
          className="lg:pb-5 pb-28 overflow-y-scroll scrollbar-hide"
          style={{ maxHeight: scrollHeight || '100%' }}
        >
          <div
            className={`relative md:px-[30px] px-4 transition-colors  duration-200 mt-2 ${isDragOver ? 'border-2 border-dashed border-blue-400 bg-blue-500/5 rounded-xl p-4 mx-4 md:mx-[30px] h-full' : ''} ${sortedFiles.length === 0 ? 'min-h-[60vh]' : ''}`}
            style={{ minHeight: 'calc(100dvh - 424px)' }}
          >
            <FileExplorerContent
              dragOverIndex={fm.dragOverIndex}
              draggedFiles={fm.draggedFiles}
              fileRefs={fm.fileRefs}
              files={sortedFiles}
              selectedFiles={fm.selectedFiles}
              trashMode={trashMode}
              viewMode={viewMode}
              onCopy={handleBulkCopyOriginal}
              onDelete={handleBulkDeleteOriginal}
              onDeleteForever={handleDeleteForeverSingle}
              onDownload={handleBulkDownloadOriginal}
              onDragEnd={handleDragEnd}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onFileClick={handleFileClick}
              onFileDoubleClick={handleFileDoubleClick}
              onFileDrop={handleFileDrop}
              onRestore={handleRestoreSingle}
              onSelectionChange={handleSelectionChange}
              onToggleFavorite={handleBulkToggleFavoriteOriginal}
              onContextMenu={(e, fileId) => {
                // Close any existing new context menu
                setNewContextMenu(prev => ({ ...prev, isOpen: false }))
                // Find the file that was right-clicked
                const clickedFile = fm.files.find(f => f.id === fileId)

                // Check if any selected files are not encrypted (status !== 'AVAILABLE')
                const selectedFiles = Array.from(fm.selectedFiles)
                  .map(id => fm.files.find(f => f.id === id))
                  .filter(Boolean)

                // If the clicked file is selected, check all selected files
                // If the clicked file is not selected, check only the clicked file
                const filesToCheck = fm.selectedFiles.has(fileId) ? selectedFiles : [clickedFile]

                // Check if any file is not available (status !== 'AVAILABLE') and not a ghost file
                // Ghost files (UPLOADING/ENCRYPTING/FAILED) should be allowed to open context menu
                const hasUnavailableFiles = filesToCheck.some(
                  file =>
                    file &&
                    file.status !== 'AVAILABLE' &&
                    file.status !== 'UPLOADING' &&
                    file.status !== 'ENCRYPTING' &&
                    file.status !== 'FAILED',
                )

                // If there are unavailable files (and they're not ghost files), don't open the context menu
                if (hasUnavailableFiles) {
                  e.preventDefault()
                  toast.error('Context menu is not available for files that are not available yet')

                  return
                }

                fm.setContextMenu({
                  isOpen: true,
                  position: { x: e.clientX, y: e.clientY },
                  fileId,
                  path: fm.files.find(f => f.id === fileId)?.path || '',
                  submenuOpen: null,
                })
              }}
              onDeleteGhostFiles={async (fileIds: string[]) => {
                // Update frontend upload tracker state for each file
                for (const serverFileId of fileIds) {
                  // Find the uploading file by serverFileId
                  const uploadingFile = uploadStore.uploadingFiles.find(
                    f => f.serverFileId === serverFileId,
                  )

                  if (uploadingFile) {
                    await handleAbort(uploadingFile.id as number)
                  }
                }
                // Also call API to abort on backend
                await abortUploads({ fileIds })
              }}
              onMove={file => {
                useActiveModelStore.setState({
                  data: {
                    selectedItems: [
                      {
                        id: file.id.toString(),
                        path: file.path || `/${file.name}`,
                        name: file.name,
                        isDir: file.type === 'folder',
                        status: 'ACTIVE',
                        sizeBytes: file.size,
                        isFavorite: file.isFavorite || false,
                        mimeType: file.type === 'folder' ? undefined : file.type,
                        mediaType: file.type === 'folder' ? 'FOLDER' : 'OTHER',
                        createdAt: file.lastModified,
                        updatedAt: file.lastModified,
                        parentId,
                      },
                    ],
                    currentFolderId: parentId,
                    currentFolderPath: breadcrumbItems?.[breadcrumbItems.length - 1]?.path,
                  },
                  width: 'max-w-[600px]',
                  scrollBehavior: 'inside',
                  modelActive: ModelControlEnum.MOVE_ITEMS,
                  toggleModel: true,
                })
              }}
              onRename={(fileIds: string[], path?: string) =>
                handleRenameOriginal(fileIds, path || '')
              }
            />

            {isFetchingNextPage && (
              <div className="flex justify-center py-4 text-gray-600 dark:text-gray-400">
                Loading more files...
              </div>
            )}

            {!hasNextPage && sortedFiles.length > 0 && (
              <div className="flex justify-center py-2 text-xs text-gray-400">No more files</div>
            )}

            <div ref={bottomRef} style={{ height: 1 }} />
          </div>
        </div>

        {!trashMode && (
          <NewContextMenu
            isOpen={newContextMenu.isOpen}
            position={newContextMenu.position}
            onAction={handleNewContextMenuAction}
            onClose={() => setNewContextMenu(prev => ({ ...prev, isOpen: false }))}
            onRequestClose={() => setNewContextMenu(prev => ({ ...prev, isOpen: false }))}
          />
        )}

        {isMobile ? (
          <MobileActionDrawer
            fileId={fm.contextMenu.fileId != null ? String(fm.contextMenu.fileId) : undefined}
            isOpen={fm.contextMenu.isOpen}
            path={fm.contextMenu.path || ''}
            trashMode={trashMode}
            fileData={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)
                : undefined
            }
            fileStatus={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.status
                : undefined
            }
            isFolder={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.isDir === true
                : false
            }
            systemFolderType={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.systemFolderType
                : undefined
            }
            onAction={onAction}
            onClose={() => fm.setContextMenu(prev => ({ ...prev, isOpen: false }))}
            onDeleteGhostFiles={async (fileIds: string[]) => {
              // Update frontend upload tracker state for each file
              for (const serverFileId of fileIds) {
                // Find the uploading file by serverFileId
                const uploadingFile = uploadStore.uploadingFiles.find(
                  f => f.serverFileId === serverFileId,
                )

                if (uploadingFile) {
                  await handleAbort(uploadingFile.id as number)
                }
              }
              // Also call API to abort on backend
              await abortUploads({ fileIds })
            }}
          />
        ) : (
          <ContextMenu
            fileId={fm.contextMenu.fileId != null ? String(fm.contextMenu.fileId) : undefined}
            isMarqueeSelecting={fm.isMarqueeSelecting}
            isOpen={fm.contextMenu.isOpen}
            path={fm.contextMenu.path || ''}
            position={fm.contextMenu.position}
            selectedFileIds={Array.from(fm.selectedFiles)}
            showRestore={showRestore}
            trashMode={trashMode}
            fileData={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)
                : undefined
            }
            fileStatus={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.status
                : undefined
            }
            isFolder={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.isDir === true
                : false
            }
            systemFolderType={
              fm.contextMenu.fileId !== null
                ? fm.files.find(f => f.id === fm.contextMenu.fileId)?.systemFolderType
                : undefined
            }
            onAction={onAction}
            onClose={() => fm.setContextMenu(prev => ({ ...prev, isOpen: false }))}
            onRequestClose={() => fm.setContextMenu(prev => ({ ...prev, isOpen: false }))}
            onDeleteGhostFiles={async (fileIds: string[]) => {
              // Update frontend upload tracker state for each file
              for (const serverFileId of fileIds) {
                // Find the uploading file by serverFileId
                const uploadingFile = uploadStore.uploadingFiles.find(
                  f => f.serverFileId === serverFileId,
                )

                if (uploadingFile) {
                  await handleAbort(uploadingFile.id as number)
                }
              }
              // Also call API to abort on backend
              await abortUploads({ fileIds })
            }}
          />
        )}

        <div className="mx-4">
          <FileExplorerDragOverlay
            breadcrumbItems={breadcrumbItems}
            clipboardFileIds={clipboardFileIds}
            isDragOver={isDragOver}
            isDraggingFile={fm.isDraggingFile}
            parentId={parentId}
            selectedFilesCount={fm.selectedFiles.size}
            onClearClipboard={clearClipboard}
          />
        </div>
      </MarqueeSelection>

      {/* Hidden File Input for folder/file uploads */}
      <input
        ref={fileInputRef}
        multiple
        accept="*/*"
        className="hidden"
        type="file"
        onChange={handleFileChange}
      />
    </div>
  )
}

export default FileExplorer
